import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createLcElement as createLcElementService,
  updateLcElement as updateLcElementService,
  deleteLcElement as deleteLcElementService, 
  getLcElements
} from "../../services/curriculum/lc-elements.service";
import { validate } from "../../utils";
import { handlerErrorV2 } from "../../utils/errors";
import { changedCurriculum, updateLcElementsList, updateLcElementsListMethod } from "../../store/actions/curriculum.actions";
import isEmpty from 'lodash/isEmpty';
import { useParams } from "react-router-dom";

const useLcElement = () => {
  const { curriculumId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const dispatch = useDispatch();
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchLcElement = useCallback(async () => {
    setIsLoadingList(true);
    try {
      dispatch(updateLcElementsList([]));
      const response = await getLcElements(curriculumId);
      dispatch(updateLcElementsList(response?.data || []));
    } catch (error) {
      setErrors({ message: error.message || 'An error ocurred'});
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchLcElement();
  }, []);

  const validateForm = (form) => {
    const fieldRules = {
      name: 'required',
      shortName: 'required|withoutSpaces',
      position: 'required|numeric'
    }

    const errors = validate(fieldRules, form)
    if (!isEmpty(errors)) {
      return { error: true, ...errors }
    }

    return { error: false }
  }

  const createLcElement = async (form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await createLcElementService(curriculumId, form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateLcElementsListMethod('POST', response.data));
        enqueueSnackbar('learning continuum element created successfully', { variant: 'success' })
        dispatch(changedCurriculum());
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const updateLcElement = async (markingId, form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await updateLcElementService(curriculumId, markingId, form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateLcElementsListMethod('PUT', response.data));
        enqueueSnackbar('learning continuum element updated successfully', { variant: 'success' })
        dispatch(changedCurriculum());
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const deleteLcElement = async (markingId) => {
    try {
      const response = await deleteLcElementService(curriculumId, markingId);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateLcElementsListMethod('DELETE', markingId));
        enqueueSnackbar('learning continuum element deleted successfully', { variant: 'success' })
        dispatch(changedCurriculum());
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  return {
    errors,
    isLoadingList,
    createLcElement,
    updateLcElement,
    deleteLcElement
  }
};

export default useLcElement;